import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Tag, FileText, Calculator, Wrench, Trophy, Lock, TrendingUp, TrendingDown, Minus, Mail, Loader2, Send, ClipboardList, Clock, Handshake } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const WALLS = [
  { key: "lower_price", label: "Lower price", icon: Tag, color: "#34D399" },
  { key: "carfax", label: "CARFAX report", icon: FileText, color: "#7FB4EE" },
  { key: "payment", label: "Payment calculator", icon: Calculator, color: "#C4B5FD" },
  { key: "estimate", label: "Repair estimate", icon: Wrench, color: "#FBBF24" },
  { key: "recon", label: "Recon report", icon: ClipboardList, color: "#7FC3FF" },
  { key: "time_on_lot", label: "Time on lot", icon: Clock, color: "#F9A8D4" },
  { key: "acquisition", label: "How we got it", icon: Handshake, color: "#FDBA74" },
];

const weekLabel = (iso) => new Date(`${iso}T12:00:00`).toLocaleDateString(undefined, { month: "short", day: "numeric" });

function Trend({ now, prev }) {
  if (now === prev) return <span className="inline-flex items-center gap-1 text-slate-500"><Minus className="h-3 w-3" /> same as last week</span>;
  const up = now > prev;
  return (
    <span className={`inline-flex items-center gap-1 ${up ? "text-emerald-400" : "text-amber-400"}`}>
      {up ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />} {up ? "+" : ""}{now - prev} vs last week ({prev})
    </span>
  );
}

function DigestFooter() {
  const { user } = useAuth();
  const [info, setInfo] = useState(null);
  const [sending, setSending] = useState(false);
  const canSend = user?.role === "admin" || user?.platform_owner || user?.platform_admin;

  const load = () => api.get("/dealer/wall-digest").then(({ data }) => setInfo(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const sendNow = async (current) => {
    setSending(true);
    try {
      const { data } = await api.post(`/dealer/wall-digest/send${current ? "?current=true" : ""}`);
      toast.success(`Digest sent to ${data.recipients.join(", ")}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  if (!info) return null;
  const last = info.last_sent;
  const lastWeekEmpty = info.preview?.last_total === 0 && (info.this_week?.last_total || 0) > 0;
  return (
    <div className="mt-5 pt-4 border-t border-[#2A2E3D] flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid="wall-digest-footer">
      <div className="text-xs text-slate-400 flex items-start gap-2">
        <Mail className="h-3.5 w-3.5 mt-0.5 text-[#0A8CF2] shrink-0" />
        <span>
          <b className="text-slate-200">Weekly wall digest</b> · {info.schedule} to {info.recipients.length} dealer admin{info.recipients.length === 1 ? "" : "s"}.{" "}
          {last ? (
            <span data-testid="wall-digest-last-sent">Last sent {new Date(last.sent_at).toLocaleString()} ({last.total} unlock{last.total === 1 ? "" : "s"}{last.winner ? `, ${last.winner.replace("_", " ")} won` : ""}).</span>
          ) : (
            <span data-testid="wall-digest-never">First digest goes out next Monday.</span>
          )}{" "}
          <a href={`${API}/dealer/wall-digest/preview${lastWeekEmpty ? "?current=true" : ""}`} target="_blank" rel="noreferrer" data-testid="wall-digest-preview-link" className="text-[#7FC3FF] underline underline-offset-2">Preview the email</a>
        </span>
      </div>
      {canSend && (
        <button onClick={() => sendNow(lastWeekEmpty)} disabled={sending} data-testid="wall-digest-send-btn"
          className="inline-flex items-center gap-1.5 rounded-md border border-[#0A8CF2]/50 bg-[#0A8CF2]/10 hover:bg-[#0A8CF2]/25 text-[#7FC3FF] px-3 h-8 text-xs font-semibold shrink-0 disabled:opacity-60">
          {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} {lastWeekEmpty ? "Email me this week so far" : "Email me last week's digest now"}
        </button>
      )}
    </div>
  );
}

export function WallInsightsPanel() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get("/stats/walls").then(({ data }) => setData(data)).catch(() => setError(true));
  }, []);

  if (error || !data) return null;
  const anyOn = WALLS.some((w) => data.walls[w.key]?.enabled);
  const anyLeads = data.wall_leads_30d > 0 || WALLS.some((w) => data.walls[w.key]?.total > 0);
  const chart = data.weekly.map((w) => ({ ...w, label: weekLabel(w.week_start) }));
  const currentWeek = data.weekly[data.weekly.length - 1]?.week_start;

  return (
    <section data-testid="wall-insights-panel" className="mb-6 rounded-xl border border-[#2A2E3D] bg-[#12141C] p-5">
      <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4 mb-4">
        <div>
          <div className="flex items-center gap-2 text-xs font-mono-tech uppercase tracking-widest text-[#0A8CF2]">
            <Lock className="h-3.5 w-3.5" /> Wall insights · last 30 days
          </div>
          <h2 className="font-heading text-xl font-bold mt-1" data-testid="wall-insights-headline">
            {data.top_wall
              ? <>Your <span className="text-[#7FC3FF]">{WALLS.find((w) => w.key === data.top_wall)?.label}</span> wall wins the most leads</>
              : anyOn ? "No wall unlocks yet — they'll show up here as shoppers leave their details" : "Lead walls are off"}
          </h2>
          <p className="text-xs text-slate-400 mt-1">
            {anyLeads
              ? <>{data.wall_leads_30d} of {data.all_leads_30d} leads ({data.wall_share_30d}%) came through a wall{data.lower_price_savings_30d > 0 ? <> · ${data.lower_price_savings_30d.toLocaleString()} in lower-price offers unlocked</> : null}.</>
              : <>Turn walls on in <Link to="/dashboard/settings" className="text-[#7FC3FF] underline underline-offset-2">Settings</Link> — shoppers trade their contact info for a lower price, the CARFAX or your payment calculator.</>}
          </p>
        </div>
        <div className="rounded-xl border border-emerald-700/40 bg-emerald-950/30 px-4 py-3 lg:min-w-[260px]" data-testid="wall-insights-this-week">
          <div className="text-[10px] uppercase tracking-widest text-emerald-400 font-mono-tech">Lower price unlocked this week</div>
          <div className="font-heading text-3xl font-black mt-0.5">{data.this_week_lower_price} <span className="text-sm font-semibold text-slate-400">shopper{data.this_week_lower_price === 1 ? "" : "s"}</span></div>
          <div className="text-[11px] mt-1"><Trend now={data.this_week_lower_price} prev={data.last_week_lower_price} /></div>
        </div>
      </div>

      <div className="grid sm:grid-cols-3 lg:grid-cols-4 gap-3 mb-5">
        {WALLS.map((w) => {
          const s = data.walls[w.key] || {};
          const win = data.top_wall === w.key;
          return (
            <div key={w.key} data-testid={`wall-tile-${w.key}`} className={`rounded-lg border px-4 py-3 ${win ? "border-[#0A8CF2]/60 bg-[#0A8CF2]/10" : "border-slate-800 bg-slate-950"}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="flex items-center gap-2 text-sm font-medium"><w.icon className="h-4 w-4" style={{ color: w.color }} /> {w.label}</span>
                {win ? (
                  <span className="inline-flex items-center gap-1 text-[10px] font-mono-tech uppercase tracking-widest text-amber-300" data-testid={`wall-winner-${w.key}`}><Trophy className="h-3 w-3" /> top</span>
                ) : !s.enabled ? (
                  <span className="text-[10px] font-mono-tech uppercase tracking-widest text-slate-500" data-testid={`wall-off-${w.key}`}>off</span>
                ) : null}
              </div>
              <div className="flex items-baseline gap-2 mt-2">
                <span className="font-heading text-2xl font-black" data-testid={`wall-30d-${w.key}`}>{s.last_30d ?? 0}</span>
                <span className="text-[11px] text-slate-500">leads · 30d</span>
              </div>
              <div className="text-[11px] text-slate-500 mt-0.5">{s.last_7d ?? 0} this week · {s.total ?? 0} all-time</div>
            </div>
          );
        })}
      </div>

      <div>
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-tech mb-2">Lower-price unlocks per week</div>
        <div className="h-36" data-testid="wall-weekly-chart">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chart} margin={{ top: 4, right: 4, left: -24, bottom: 0 }} barCategoryGap="28%">
              <XAxis dataKey="label" tick={{ fill: "#64748B", fontSize: 11 }} axisLine={{ stroke: "#2A2E3D" }} tickLine={false} />
              <YAxis allowDecimals={false} tick={{ fill: "#64748B", fontSize: 11 }} axisLine={false} tickLine={false} />
              <Tooltip
                cursor={{ fill: "rgba(255,255,255,0.04)" }}
                contentStyle={{ background: "#0F111A", border: "1px solid #2A2E3D", borderRadius: 8, fontSize: 12 }}
                labelStyle={{ color: "#CBD5E1" }}
                formatter={(value, name) => [value, WALLS.find((w) => w.key === name)?.label || name]}
                labelFormatter={(l) => `Week of ${l}`}
              />
              <Bar dataKey="lower_price" name="lower_price" radius={[4, 4, 0, 0]}>
                {chart.map((w) => <Cell key={w.week_start} fill={w.week_start === currentWeek ? "#34D399" : "#0A8CF2"} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      <DigestFooter />
    </section>
  );
}
